import React, { HTMLAttributes, useEffect, useMemo } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AutoComplete from '@mui/material/Autocomplete';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  InsightViewCellColorItem,
  InsightViewCellColorRuleItem,
} from 'types/api/SystemTenant/AROView/InsightViewDefinitionTypes';
import { MAP_COLOR_CODE_WITH_COLOR_HEXA } from 'constant/ViewConstant';
import { useGetStandardViewCubeAttributesDropdown } from 'services/v1/SystemTenant/AROView/StandardViewDefinitionService';
import { CustomDataTable } from 'components/DatatableComponent';
import { AddCircleOutlineOutlined, Edit as EditIcon } from '@mui/icons-material';
import { GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { ModalCellColorRule, isBetweenCondition, CONDITION_SYMBOL } from './ModalCellColorRule';

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 900,
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export interface ModalAddCellColorDefinitionProps {
  visible?: boolean;
  onClose?: () => void;
  onSave?: (attributeItems: InsightViewCellColorItem[]) => void;
  onDelete?: (attributeItem: InsightViewCellColorItem) => void;
  selectedData?: InsightViewCellColorItem;
  usedCubeAttributeIds: Set<string>;
  cubeId?: string;
}

function ColorSwatch(props: { value: string | null; size?: number }) {
  const { value, size = 18 } = props;
  if (!value) return null;
  return (
    <span
      style={{
        width: size,
        height: size,
        backgroundColor: MAP_COLOR_CODE_WITH_COLOR_HEXA.get(value),
        display: 'inline-block',
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.12)',
        flexShrink: 0,
      }}
    />
  );
}

const renderOption = (props: HTMLAttributes<HTMLElement>, option: AutoCompleteItem) => {
  return (
    <Box component='li' sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }} {...props}>
      <span>{option.label}</span>
    </Box>
  );
};

function RuleDescription(props: { rule: InsightViewCellColorRuleItem }) {
  const { rule } = props;
  if (!rule.condition || !rule.color) {
    return (
      <Typography variant='input-label-gray' fontStyle='italic'>
        Incomplete rule
      </Typography>
    );
  }
  const between = isBetweenCondition(rule.condition);
  const grayText = { color: '#828C99' };

  return (
    <Stack direction='row' alignItems='center' spacing={0.75} flexWrap='wrap' sx={{ py: 0.5 }}>
      <Typography variant='body2' sx={grayText}>
        If
      </Typography>
      {between ? (
        <>
          <Typography variant='body2' fontWeight={600}>
            {rule.minValue || '?'}
          </Typography>
          <Typography variant='body2' sx={grayText}>
            {rule.includeMin ? '≤' : '<'}
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            value
          </Typography>
          <Typography variant='body2' sx={grayText}>
            {rule.includeMax ? '≤' : '<'}
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            {rule.maxValue || '?'}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant='body2' fontWeight={600}>
            value
          </Typography>
          <Typography variant='body2' sx={grayText}>
            {CONDITION_SYMBOL[rule.condition.value as string] ?? rule.condition.label}
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            {rule.attributeReference ? rule.attributeReference.label : rule.value || '?'}
          </Typography>
        </>
      )}
      <Typography variant='body2' sx={grayText}>
        →
      </Typography>
      <ColorSwatch value={rule.color.value as string} />
      <Typography variant='body2' fontWeight={600}>
        {rule.color.label}
      </Typography>
    </Stack>
  );
}

export function ModalAddCellColor(props: Readonly<ModalAddCellColorDefinitionProps>) {
  const { onClose, visible = false, onSave, onDelete, selectedData, cubeId, usedCubeAttributeIds } = props;

  const [cubeAttribute, setCubeAttribute] = React.useState<AutoCompleteItem | null>(null);
  const [rules, setRules] = React.useState<InsightViewCellColorRuleItem[]>([]);

  const [ruleModalOpen, setRuleModalOpen] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState<InsightViewCellColorRuleItem | null>(null);

  const { data: cubeAttributeDropdown } = useGetStandardViewCubeAttributesDropdown(cubeId);

  const cubeDropdownOptions = useMemo<AutoCompleteItem[]>(() => {
    if (!cubeAttributeDropdown) return [];
    return cubeAttributeDropdown.data.filter((item) => !usedCubeAttributeIds.has(item.value.toString()));
  }, [cubeAttributeDropdown, usedCubeAttributeIds]);

  const handleOnCloseModal = () => {
    onClose?.();
    setCubeAttribute(null);
    setRules([]);
    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const handleOnDelete = () => {
    onDelete?.(selectedData as InsightViewCellColorItem);
    handleOnCloseModal();
  };

  const handleOnSave = () => {
    const item: InsightViewCellColorItem = {
      id: selectedData?.id ?? `CELLCOLOR-${crypto.randomUUID()}`,
      cubeAttribute,
      rules: rules.map((rule, idx) => ({ ...rule, priorityOrder: idx })),
    };
    onSave?.([item]);
    handleOnCloseModal();
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setRuleModalOpen(true);
  };

  const handleEditRule = (id: string) => {
    const rule = rules.find((r) => r.id === id);
    if (!rule) return;
    setEditingRule(rule);
    setRuleModalOpen(true);
  };

  const handleDeleteRule = (id: string) => {
    setRules((prev) => prev.filter((row) => row.id !== id));
  };

  const handleRuleModalSave = (incoming: InsightViewCellColorRuleItem) => {
    setRules((prev) => {
      if (incoming.id && prev.some((r) => r.id === incoming.id)) {
        return prev.map((r) => (r.id === incoming.id ? { ...incoming } : r));
      }
      const newId = incoming.id ?? `RULE-${crypto.randomUUID()}`;
      return [...prev, { ...incoming, id: newId, priorityOrder: prev.length }];
    });
    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const handleRuleModalClose = () => {
    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const usedAttributeRefs = useMemo(() => {
    const used = new Set<string>();
    if (cubeAttribute) used.add(cubeAttribute.value.toString());
    rules.forEach((row) => {
      if (row.attributeReference) used.add(row.attributeReference.value.toString());
    });
    return used;
  }, [cubeAttribute, rules]);

  const ruleAttributeReferenceOptions = useMemo<AutoCompleteItem[]>(() => {
    const currentRef = editingRule?.attributeReference;
    return cubeDropdownOptions.filter((opt) => {
      if (currentRef && currentRef.value === opt.value) return true;
      return !usedAttributeRefs.has(opt.value.toString());
    });
  }, [cubeDropdownOptions, usedAttributeRefs, editingRule]);

  const columns = [
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <IconButton onClick={() => handleEditRule(params.row.id)} size='small'>
            <EditIcon fontSize='small' />
          </IconButton>
          <IconButton onClick={() => handleDeleteRule(params.row.id)} size='small'>
            <HighlightOffRoundedIcon fontSize='small' />
          </IconButton>
        </Stack>
      ),
    },
    {
      field: 'priorityOrder',
      headerName: 'Priority',
      width: 90,
      sortable: false,
      renderCell: (params: any) => (
        <Box
          sx={{
            bgcolor: '#EDF1F7',
            color: '#3B4797',
            fontWeight: 600,
            px: 1.25,
            py: 0.25,
            borderRadius: 1,
            fontSize: 12,
          }}
        >
          P{params.value + 1}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Rule',
      flex: 1,
      minWidth: 460,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => <RuleDescription rule={params.row} />,
    },
    {
      field: 'attributeReference',
      headerName: 'Reference',
      width: 180,
      sortable: false,
      renderCell: (params: any) =>
        params.value ? (
          <Typography variant='input-label-gray' fontStyle='italic'>
            vs {params.value.label}
          </Typography>
        ) : (
          ''
        ),
    },
  ];

  const isValidForm =
    cubeAttribute &&
    rules.length > 0 &&
    rules.every((r) => {
      if (!r.condition || !r.color) return false;
      if (isBetweenCondition(r.condition)) {
        return r.minValue != null && r.minValue !== '' && r.maxValue != null && r.maxValue !== '';
      }
      return r.value != null && r.value !== '';
    });

  useEffect(() => {
    if (!visible) return;
    if (selectedData) {
      setCubeAttribute(selectedData.cubeAttribute);
      setRules(selectedData.rules ? selectedData.rules.map((rule, idx) => ({ ...rule, priorityOrder: idx })) : []);
    } else {
      setCubeAttribute(null);
      setRules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData, visible]);

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    const { oldIndex, targetIndex } = params;
    if (oldIndex === targetIndex) return;

    setRules((prevRules) => {
      const newRules = [...prevRules];
      const [movedRule] = newRules.splice(oldIndex, 1);
      newRules.splice(targetIndex, 0, movedRule);

      return newRules.map((rule, index) => ({
        ...rule,
        priorityOrder: index,
      }));
    });
  };

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        <Stack direction='column' sx={ModalContentStyle} px={2}>
          <Stack>
            <Stack justifyContent='space-between' direction='row' alignItems='center'>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                Cell Color Definition
              </Typography>
              <IconButton onClick={handleOnCloseModal} component='label'>
                <HighlightOffRoundedIcon sx={closeIconStyle} />
              </IconButton>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 1 }} />

          <Stack mb={1.6} width='70%'>
            <Typography variant='body2' fontWeight='bold' color='black'>
              Selected Attribute
            </Typography>
            <Typography variant='input-label-gray' mb={1}>
              Choose the attribute for this rule to be applied to.
            </Typography>
            <AutoComplete
              clearIcon={null}
              size='small'
              value={cubeAttribute}
              onChange={(_, newValue) => setCubeAttribute(newValue)}
              getOptionLabel={(option: AutoCompleteItem) => option.label}
              options={cubeDropdownOptions}
              renderOption={renderOption}
              renderInput={(params) => (
                <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                  <TextField {...params} placeholder='Select Attribute' name='cubeAttribute' />
                </Stack>
              )}
            />
          </Stack>

          <Stack width='100%'>
            <Typography variant='body2' fontWeight='bold' color='black' mb={1}>
              Conditional Rules
            </Typography>
            <Stack width='25%' mb={2}>
              <Button variant='main-table-panel' startIcon={<AddCircleOutlineOutlined />} onClick={handleAddRule}>
                Add Rule
              </Button>
            </Stack>

            <CustomDataTable
              rows={rules}
              columns={columns}
              checkboxSelection={false}
              disableSelectionOnClick
              sortingMode='client'
              filterMode='client'
              paginationMode='client'
              density='compact'
              rowHeight={45}
              autoHeight
              sx={{ backgroundColor: '#fff', minHeight: 350 }}
              getRowId={(row) => row.id}
              components={{ Toolbar: null, Pagination: null }}
              rowReordering
              onRowOrderChange={handleRowOrderChange}
            />
          </Stack>

          <Stack px={2}>
            <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
              {selectedData && (
                <Button
                  onClick={handleOnDelete}
                  variant='outlined'
                  sx={{
                    textTransform: 'none',
                  }}
                  color='error'
                >
                  Delete
                </Button>
              )}
              <Button variant='main-table-panel-border' onClick={handleOnCloseModal}>
                Cancel
              </Button>
              <ButtonLoading variant='main-table-panel' disabled={!isValidForm} onClick={handleOnSave}>
                Save Cell Color
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>

      <ModalCellColorRule
        visible={ruleModalOpen}
        onClose={handleRuleModalClose}
        onSave={handleRuleModalSave}
        initialData={editingRule}
        attributeReferenceOptions={ruleAttributeReferenceOptions}
      />
    </div>
  );
}
